import Trix from "trix"

const trixEvents = {
    editorFields: [
      {
        selector: '[data-fui-id="jobPosting-jobDescription"]',
        inputName: 'fields[jobDescription]',
        options: ['bold', 'italic', 'link', 'bullet', 'number']
      },
      {
        selector: '[data-fui-id="jobPosting-gearSoftwareNeedsDescription"]',
        inputName: 'fields[gearSoftwareNeedsDescription]',
        options: ['bold', 'italic', 'link', 'bullet', 'number']
      },
      {
        selector: '[data-redactor-id="message-body"]',
        inputName: 'message-body',
        options: ['bold', 'italic', 'link', 'bullet', 'number']
      },
      {
        selector: '[data-prjs-richtext]',
        inputName: 'fields*',
        options: ['bold', 'italic', 'link']
      }
    ],
    init() {
      this.configureTrix();
      this.initializeAllEditors();
    },

    // Configure Trix globally (only needs to be done once)
    configureTrix() {
      if (this.configured) return;
      
      // Register custom underline attribute
      Trix.config.textAttributes.underline = {
        tagName: 'u',
        inheritable: true,
        parser(element) {
          return element.tagName === 'U' || element.style.textDecoration === 'underline';
        }
      };

      // Set default toolbar HTML
      Trix.config.toolbar.getDefaultHTML = () => {
        return `
          <div class="trix-button-row">
            <span class="trix-button-group trix-button-group--text-tools" data-trix-button-group="text-tools">
              <button type="button" class="trix-button trix-button--icon trix-button--icon-bold" data-trix-attribute="bold" data-trix-key="b" title="Bold" tabindex="-1">Bold</button>
              <button type="button" class="trix-button trix-button--icon trix-button--icon-italic" data-trix-attribute="italic" data-trix-key="i" title="Italic" tabindex="-1">Italic</button>
              <button type="button" class="trix-button trix-button--icon trix-button--icon-link" data-trix-attribute="href" data-trix-action="link" data-trix-key="k" title="Link" tabindex="-1">Link</button>
              <button type="button" class="trix-button trix-button--icon trix-button--icon-bullet-list" data-trix-attribute="bullet" title="Bullets" tabindex="-1">Bullets</button>
              <button type="button" class="trix-button trix-button--icon trix-button--icon-number-list" data-trix-attribute="number" title="Numbers" tabindex="-1">Numbers</button>
            </span>
          </div>
          <div class="trix-dialogs" data-trix-dialogs>
            <div class="trix-dialog trix-dialog--link" data-trix-dialog="href" data-trix-dialog-attribute="href">
              <div class="trix-dialog__link-fields">
                <input type="url" name="href" class="trix-input trix-input--dialog" placeholder="https://" aria-label="Link" data-trix-validate-href data-trix-input>
                <div class="trix-button-group">
                  <input type="button" class="trix-button trix-button--dialog" value="Link" data-trix-method="setAttribute">
                  <input type="button" class="trix-button trix-button--dialog" value="Unlink" data-trix-method="removeAttribute">
                </div>
              </div>
            </div>
          </div>
        `;
      };
      
      this.configured = true;
    },

    // Initialize all Trix editors in the document
    initializeAllEditors() {
      this.editorFields.forEach((editor, index) => {
        this.initializeEditorsInContainer(document, editor, index);
      });
    },

    // Initialize Trix editors in a specific container (for Alpine.js)
    initializeInContainer(container) {
      if (!container) {
        console.log('Trix initializeInContainer: No container provided');
        return;
      }
      
      console.log('Trix initializeInContainer: Starting initialization for container:', container);
      
      this.configureTrix(); // Ensure Trix is configured
      
      this.editorFields.forEach((editor, index) => {
        console.log(`Trix: Looking for ${editor.selector} in container`);
        const elements = container.querySelectorAll(editor.selector);
        console.log(`Trix: Found ${elements.length} elements for ${editor.selector}`);
        elements.forEach(el => {
          console.log('Trix: Element found:', el.tagName, el.type, el.getAttribute('data-prjs-richtext'));
        });
        this.initializeEditorsInContainer(container, editor, index);
      });
    },

    // Helper method to initialize editors for a specific editor config in a container
    initializeEditorsInContainer(container, editor, baseIndex) {
      const originalElements = container.querySelectorAll(editor.selector);
      originalElements.forEach((originalElement, elementIndex) => {
        // Skip if already initialized
        if (originalElement.dataset.trixInitialized) return;
        
        // Create unique IDs
        const uniqueId = `${baseIndex}-${elementIndex}-${Date.now()}`;
        const hiddenInputId = `trix-input-${uniqueId}`;
        const trixEditorId = `trix-editor-${uniqueId}`;
        const toolbarId = `trix-toolbar-${uniqueId}`;
        
        // Get the original value if it exists
        const originalValue = originalElement.value || originalElement.innerHTML || '';
        
        // Create visually hidden text input for form submission (not type="hidden" so :user-invalid works)
        const hiddenInput = document.createElement('input');
        hiddenInput.type = 'text';
        hiddenInput.id = hiddenInputId;
        hiddenInput.name = originalElement.name || editor.inputName;
        hiddenInput.value = originalValue;
        hiddenInput.style.cssText = 'position: absolute; opacity: 0; pointer-events: none; height: 0; width: 0;';
        hiddenInput.setAttribute('tabindex', '-1');
        hiddenInput.setAttribute('aria-hidden', 'true');
        
        // Copy all attributes from original element to hidden input
        Array.from(originalElement.attributes).forEach(attr => {
          // Skip attributes that we've already set or that don't make sense for hidden inputs
          if (!['type', 'id', 'name', 'value', 'placeholder', 'rows', 'cols', 'class'].includes(attr.name)) {
            hiddenInput.setAttribute(attr.name, attr.value);
          }
        });
        
        // Create Trix editor element
        const trixEditor = document.createElement('trix-editor');
        trixEditor.id = trixEditorId;
        trixEditor.setAttribute('input', hiddenInputId);
        trixEditor.setAttribute('placeholder', originalElement.placeholder || 'Enter text...');
        
        // Copy any classes from the original element
        if (originalElement.className) {
          trixEditor.className = originalElement.className;
        }
        
        // Build a per-editor toolbar based on allowed options
        const allowedOptions = Array.isArray(editor.options) ? editor.options : [];
        const toolbar = document.createElement('trix-toolbar');
        toolbar.id = toolbarId;

        const buttonGroups = [''];
        let currentGroupIndex = 0;
        allowedOptions.forEach((opt) => {
          switch (opt) {
            case 'bold':
              buttonGroups[currentGroupIndex] += '<button type="button" class="trix-button trix-button--icon trix-button--icon-bold" data-trix-attribute="bold" data-trix-key="b" title="Bold" tabindex="-1">Bold</button>';
              break;
            case 'italic':
              buttonGroups[currentGroupIndex] += '<button type="button" class="trix-button trix-button--icon trix-button--icon-italic" data-trix-attribute="italic" data-trix-key="i" title="Italic" tabindex="-1">Italic</button>';
              break;
            case 'link':
              buttonGroups[currentGroupIndex] += '<button type="button" class="trix-button trix-button--icon trix-button--icon-link" data-trix-attribute="href" data-trix-action="link" data-trix-key="k" title="Link" tabindex="-1">Link</button>';
              break;
            case 'bullet':
              buttonGroups[currentGroupIndex] += '<button type="button" class="trix-button trix-button--icon trix-button--icon-bullet-list" data-trix-attribute="bullet" title="Bullets" tabindex="-1">Bullets</button>';
              break;
            case 'number':
              buttonGroups[currentGroupIndex] += '<button type="button" class="trix-button trix-button--icon trix-button--icon-number-list" data-trix-attribute="number" title="Numbers" tabindex="-1">Numbers</button>';
              break;
            default:
              break;
          }
        });

        const linkDialogHTML = allowedOptions.includes('link')
          ? '<div class="trix-dialog trix-dialog--link" data-trix-dialog="href" data-trix-dialog-attribute="href">\
              <div class="trix-dialog__link-fields">\
                <input type="url" name="href" class="trix-input trix-input--dialog" placeholder="https://" aria-label="Link" data-trix-validate-href data-trix-input>\
                <div class="trix-button-group">\
                  <input type="button" class="trix-button trix-button--dialog" value="Link" data-trix-method="setAttribute">\
                  <input type="button" class="trix-button trix-button--dialog" value="Unlink" data-trix-method="removeAttribute">\
                </div>\
              </div>\
            </div>'
          : '';

        const groupsHTML = buttonGroups
          .filter(group => group !== '')
          .map(group => `<span class="trix-button-group trix-button-group--text-tools" data-trix-button-group="text-tools">${group}</span>`)
          .join('');

        toolbar.innerHTML = `
          <div class="trix-button-row">
            ${groupsHTML}
          </div>
          <div class="trix-dialogs" data-trix-dialogs>
            ${linkDialogHTML}
          </div>
        `;

        trixEditor.setAttribute('toolbar', toolbarId);
        
        // Replace the original element
        originalElement.parentNode.insertBefore(hiddenInput, originalElement);
        originalElement.parentNode.insertBefore(toolbar, originalElement);
        originalElement.parentNode.insertBefore(trixEditor, originalElement);
        
        // Add custom validation for required fields
        if (hiddenInput.hasAttribute('required')) {
          // Validation function to check if content is truly empty
          const validateContent = () => {
            if (!trixEditor.editor) return;
            
            const textContent = trixEditor.editor.getDocument().toString().trim();
            const isEmpty = textContent === '';
            
            if (isEmpty) {
              hiddenInput.value = '';
              hiddenInput.setCustomValidity('This field is required');
            } else {
              hiddenInput.setCustomValidity('');
            }
          };
          
          // Validate on content change
          trixEditor.addEventListener('trix-change', validateContent);
          
          // Set initial validation state after Trix is fully initialized
          trixEditor.addEventListener('trix-initialize', validateContent);
          
          // Also validate on blur to catch when user leaves empty field
          trixEditor.addEventListener('trix-blur', validateContent);
        }
        
        // Mark as initialized before removing to avoid re-initialization
        originalElement.dataset.trixInitialized = 'true';
        originalElement.remove();
      });
    },

}

// Add global form submit validation for Trix required fields
document.addEventListener('submit', (e) => {
  const form = e.target;
  if (!form.matches('form')) return;
  
  const trixEditors = form.querySelectorAll('trix-editor');
  for (const editor of trixEditors) {
    const inputId = editor.getAttribute('input');
    const hiddenInput = document.getElementById(inputId);
    
    if (hiddenInput && hiddenInput.hasAttribute('required') && !hiddenInput.disabled) {
      const textContent = editor.editor ? editor.editor.getDocument().toString().trim() : '';
      
      if (textContent === '') {
        e.preventDefault();
        hiddenInput.setCustomValidity('This field is required');
        hiddenInput.reportValidity();
        editor.focus();
        return;
      }
    }
  }
}, true);

export default trixEvents;
